/* kp-heat-live.js — the two PDP heat signals move while the page is open (#64).

   Liquid prints the opening numbers (kp-run-heat, hour-seeded, so a reload
   lands near where it left). This makes them live in the session:

     - "Last reserved N min ago" gains a minute every minute.
     - Every 90–240 s an "event" fires: the in-carts count moves by −1, +1 or
       +2 (clamped to the theme setting's range), and four events in ten
       also reset "last reserved" to "just now", which then counts up again.
     - A number that changed gets .kp-heat-tick for one sweep (.55s, the one
       easing), so the eye is told exactly when something happened.

   Nothing here is stored or sent; nothing survives the tab. Under
   prefers-reduced-motion the numbers still update — they are information —
   only the sweep is dropped (CSS). Hidden tabs pause: no work off-screen,
   and no burst of ten events on return.

   Wiring (sections/main-product.liquid):
     <kp-heat-live data-cart-min data-cart-max data-just-now data-min-unit>
     with, inside it or anywhere on the page:
       [data-kp-carts]      the cart number (text is the bare number)
       [data-kp-last]       the minutes number; [data-kp-last-unit] the word
                            after it ("min ago" → "just now" at zero)
   Dawn re-renders the price and inventory blocks on a variant change; the
   element re-reads its targets on each event, so it survives that. */
(() => {
  if (customElements.get('kp-heat-live')) return;

  class KpHeatLive extends HTMLElement {
    connectedCallback() {
      this.cartMin = Number(this.dataset.cartMin || 1);
      this.cartMax = Number(this.dataset.cartMax || 99);
      this.justNow = this.dataset.justNow || 'just now';
      this.minUnit = this.dataset.minUnit || 'min ago';
      const last = document.querySelector('[data-kp-last]');
      this.minutes = last ? Number(last.dataset.kpLast || 0) : 0;
      this.timers = [];
      this.tickMinute = this.tickMinute.bind(this);
      this.event = this.event.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    start() {
      this.stop();
      this.timers.push(setInterval(this.tickMinute, 60000));
      this.scheduleEvent();
    }

    stop() {
      this.timers.forEach((t) => { clearInterval(t); clearTimeout(t); });
      this.timers = [];
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.start();
    }

    scheduleEvent() {
      clearTimeout(this.eventTimer);
      const wait = 90000 + Math.random() * 150000;
      this.eventTimer = setTimeout(this.event, wait);
      this.timers.push(this.eventTimer);
    }

    tickMinute() {
      this.minutes += 1;
      this.paintLast(false);
    }

    event() {
      const carts = document.querySelector('[data-kp-carts]');
      if (carts) {
        const cur = Number(carts.dataset.kpCarts || 0);
        const r = Math.random();
        const delta = r < 0.3 ? -1 : r < 0.8 ? 1 : 2;
        const next = Math.min(this.cartMax, Math.max(this.cartMin, cur + delta));
        if (next !== cur) {
          carts.dataset.kpCarts = String(next);
          carts.textContent = String(next);
          carts.dataset.kpDelta = (next > cur ? '+' : '−') + Math.abs(next - cur);
          this.flash(carts);
        }
      }
      if (Math.random() < 0.4) {
        this.minutes = 0;
        this.paintLast(true);
      }
      this.scheduleEvent();
    }

    paintLast(flash) {
      const last = document.querySelector('[data-kp-last]');
      const unit = document.querySelector('[data-kp-last-unit]');
      if (!last) return;
      last.dataset.kpLast = String(this.minutes);
      if (this.minutes === 0) {
        last.textContent = '';
        if (unit) unit.textContent = this.justNow;
      } else {
        last.textContent = String(this.minutes);
        if (unit) unit.textContent = this.minUnit;
      }
      if (flash) { last.dataset.kpDelta = '↺'; this.flash(last); }
    }

    flash(el) {
      el.classList.remove('kp-heat-tick');
      // Restart the animation even when the class is already present.
      void el.offsetWidth;
      el.classList.add('kp-heat-tick');
    }
  }

  customElements.define('kp-heat-live', KpHeatLive);
})();
